import { useState, useEffect } from 'react';
import { Plus, Calendar, Clock, User, MapPin, DollarSign, FileText } from 'lucide-react';
import { calendarService } from '@/services/calendarService';
import { fetchCategories, type Category } from '@/services/categoryService';
import { useGlobalToast } from '@/contexts/ToastContext';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { LocationInput } from '@/components/location/LocationInput';

interface ManualBookingProps {
  providerId: string;
  onBookingCreated?: () => void;
}

export function ManualBooking({ providerId, onBookingCreated }: ManualBookingProps) {
  const { success, error } = useGlobalToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [formData, setFormData] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    categoryId: '',
    scheduledDate: '',
    startTime: '',
    endTime: '',
    location: '',
    notes: '',
    estimatedCost: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setIsLoadingCategories(true);
      const data = await fetchCategories();
      setCategories(data);
    } catch (err) {
      console.error('Failed to load categories:', err);
      error('Error', 'Failed to load service categories');
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Customer name validation
    if (!formData.customerName.trim()) {
      newErrors.customerName = 'Customer name is required';
    } else if (formData.customerName.trim().length < 2) {
      newErrors.customerName = 'Name must be at least 2 characters';
    }

    // Phone validation (optional but must be valid if provided)
    if (formData.customerPhone.trim()) {
      const phoneRegex = /^[\d\s\+\-\(\)]+$/;
      if (!phoneRegex.test(formData.customerPhone)) {
        newErrors.customerPhone = 'Please enter a valid phone number';
      } else if (formData.customerPhone.replace(/\D/g, '').length < 7) {
        newErrors.customerPhone = 'Phone number must be at least 7 digits';
      }
    }

    // Email validation (optional but must be valid if provided)
    if (formData.customerEmail.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.customerEmail)) {
        newErrors.customerEmail = 'Please enter a valid email address';
      }
    }

    // Category validation
    if (!formData.categoryId) {
      newErrors.categoryId = 'Service category is required';
    }

    // Date validation
    if (!formData.scheduledDate) {
      newErrors.scheduledDate = 'Date is required';
    } else {
      const selectedDate = new Date(formData.scheduledDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate < today) {
        newErrors.scheduledDate = 'Date cannot be in the past';
      }
    }

    // Time validation
    if (!formData.startTime) {
      newErrors.startTime = 'Start time is required';
    }

    if (!formData.endTime) {
      newErrors.endTime = 'End time is required';
    }

    if (formData.startTime && formData.endTime) {
      if (formData.startTime >= formData.endTime) {
        newErrors.endTime = 'End time must be after start time';
      }
      
      // Check if duration is reasonable (at least 15 minutes)
      const duration = calculateDuration();
      if (duration < 15) {
        newErrors.endTime = 'Booking must be at least 15 minutes';
      }
    }

    // Location validation
    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    } else if (formData.location.trim().length < 5) {
      newErrors.location = 'Please enter a complete address';
    }

    // Estimated cost validation (optional but must be valid if provided)
    if (formData.estimatedCost && parseFloat(formData.estimatedCost) < 0) {
      newErrors.estimatedCost = 'Cost cannot be negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculateDuration = () => {
    if (!formData.startTime || !formData.endTime) return 0;
    
    const [startHours, startMinutes] = formData.startTime.split(':').map(Number);
    const [endHours, endMinutes] = formData.endTime.split(':').map(Number);
    
    const startTotalMinutes = startHours * 60 + startMinutes;
    const endTotalMinutes = endHours * 60 + endMinutes;
    
    return endTotalMinutes - startTotalMinutes;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      error('Validation Error', 'Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const duration = calculateDuration();
      
      // Find category name from ID
      const selectedCategory = categories.find(cat => cat.id === formData.categoryId);
      const serviceType = selectedCategory?.name || 'Service';
      
      await calendarService.bookTimeSlot(
        providerId,
        formData.scheduledDate,
        formData.startTime,
        formData.endTime,
        {
          customerName: formData.customerName,
          serviceType: serviceType,
          location: formData.location,
          notes: formData.notes,
          estimatedCost: formData.estimatedCost ? parseFloat(formData.estimatedCost) : undefined,
          customerId: '00000000-0000-0000-0000-000000000000', // Null UUID for manual bookings
          providerName: 'Provider', // Will be filled by backend
          // jobId is not included for manual bookings
        }
      );

      success('Booking Created', 'Manual booking has been added to your calendar');
      
      // Reset form
      setFormData({
        customerName: '',
        customerPhone: '',
        customerEmail: '',
        categoryId: '',
        scheduledDate: '',
        startTime: '',
        endTime: '',
        location: '',
        notes: '',
        estimatedCost: ''
      });
      
      onBookingCreated?.();
    } catch (err: any) {
      error('Booking Failed', err.message || 'Failed to create booking');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  return (
    <div className="max-w-4xl mx-auto">
      <Card className="p-8">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Plus className="w-6 h-6" />
            Manual Booking
          </h2>
          <p className="text-gray-600 mt-1">Add a job directly to your calendar</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Customer Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Customer Information
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Customer Name *
                </label>
                <input
                  type="text"
                  value={formData.customerName}
                  onChange={(e) => handleChange('customerName', e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                    errors.customerName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="John Doe"
                />
                {errors.customerName && (
                  <p className="text-xs text-red-600 mt-1">{errors.customerName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.customerPhone}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow only numbers, spaces, +, -, (, )
                    if (value === '' || /^[\d\s\+\-\(\)]+$/.test(value)) {
                      handleChange('customerPhone', value);
                    }
                  }}
                  onBlur={() => {
                    // Validate on blur
                    if (formData.customerPhone.trim()) {
                      const phoneRegex = /^[\d\s\+\-\(\)]+$/;
                      if (!phoneRegex.test(formData.customerPhone)) {
                        setErrors({ ...errors, customerPhone: 'Please enter a valid phone number' });
                      } else if (formData.customerPhone.replace(/\D/g, '').length < 7) {
                        setErrors({ ...errors, customerPhone: 'Phone number must be at least 7 digits' });
                      }
                    }
                  }}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                    errors.customerPhone ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="+353 123 456 789"
                />
                {errors.customerPhone && (
                  <p className="text-xs text-red-600 mt-1">{errors.customerPhone}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.customerEmail}
                  onChange={(e) => handleChange('customerEmail', e.target.value)}
                  onBlur={() => {
                    // Validate on blur
                    if (formData.customerEmail.trim()) {
                      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                      if (!emailRegex.test(formData.customerEmail)) {
                        setErrors({ ...errors, customerEmail: 'Please enter a valid email address' });
                      }
                    }
                  }}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                    errors.customerEmail ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="john@example.com"
                />
                {errors.customerEmail && (
                  <p className="text-xs text-red-600 mt-1">{errors.customerEmail}</p>
                )}
              </div>
            </div>
          </div>

          {/* Service Details */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Service Details
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service Category *
                </label>
                {isLoadingCategories ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                  </div>
                ) : (
                  <select
                    value={formData.categoryId}
                    onChange={(e) => handleChange('categoryId', e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                      errors.categoryId ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select a service category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                )}
                {errors.categoryId && (
                  <p className="text-xs text-red-600 mt-1">{errors.categoryId}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Location *
                </label>
                <LocationInput
                  value={formData.location}
                  onChange={(value) => handleChange('location', value)}
                  placeholder="Enter service address"
                />
                {errors.location && (
                  <p className="text-xs text-red-600 mt-1">{errors.location}</p>
                )}
              </div>
            </div>
          </div>

          {/* Schedule */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Schedule
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date *
                </label>
                <input
                  type="date"
                  value={formData.scheduledDate}
                  onChange={(e) => handleChange('scheduledDate', e.target.value)}
                  min={getTodayDate()}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                    errors.scheduledDate ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.scheduledDate && (
                  <p className="text-xs text-red-600 mt-1">{errors.scheduledDate}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Start Time *
                </label>
                <input
                  type="time"
                  value={formData.startTime}
                  onChange={(e) => handleChange('startTime', e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                    errors.startTime ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.startTime && (
                  <p className="text-xs text-red-600 mt-1">{errors.startTime}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Time *
                </label>
                <input
                  type="time"
                  value={formData.endTime}
                  onChange={(e) => handleChange('endTime', e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                    errors.endTime ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.endTime && (
                  <p className="text-xs text-red-600 mt-1">{errors.endTime}</p>
                )}
              </div>
            </div>

            {formData.startTime && formData.endTime && calculateDuration() > 0 && (
              <p className="text-sm text-gray-600 mt-2">
                Duration: {Math.floor(calculateDuration() / 60)}h {calculateDuration() % 60}m
              </p>
            )}
          </div>

          {/* Additional Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <DollarSign className="w-4 h-4 inline mr-1" />
                  Estimated Cost (€)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.estimatedCost}
                  onChange={(e) => handleChange('estimatedCost', e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                    errors.estimatedCost ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="150.00"
                />
                {errors.estimatedCost && (
                  <p className="text-xs text-red-600 mt-1">{errors.estimatedCost}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Any additional notes or requirements..."
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setFormData({
                  customerName: '',
                  customerPhone: '',
                  customerEmail: '',
                  categoryId: '',
                  scheduledDate: '',
                  startTime: '',
                  endTime: '',
                  location: '',
                  notes: '',
                  estimatedCost: ''
                });
                setErrors({});
              }}
            >
              Clear Form
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
            >
              <Plus className="w-4 h-4 mr-2" />
              {isSubmitting ? 'Creating...' : 'Create Booking'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
